/**
 * UTM Strategy Types
 * For managing and collaborating on UTM campaigns
 */

// Predefined website domains for the UTM builder
export const WEBSITE_DOMAINS = [
  { id: 'main', domain: 'www.lctimberframes.com', label: 'Main Website' },
  { id: 'build', domain: 'build.lctimberframes.com', label: '3D Kits' },
] as const;

// Common UTM sources
export const COMMON_UTM_SOURCES = [
  'facebook',
  'instagram',
  'google',
  'youtube',
  'linkedin',
  'twitter',
  'email',
  'sendblue',
  'chatgpt',
  'website',
  'sales',
  'direct',
  'referral',
] as const;

// Common UTM mediums
export const COMMON_UTM_MEDIUMS = [
  'social',
  'cpc',
  'cpm',
  'email',
  'sms',
  'banner',
  'affiliate',
  'organic',
  'video',
  'display',
  'retargeting',
] as const;

// A single UTM link configuration
export interface UtmLink {
  id: string;
  baseUrl: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm?: string;
  utmContent?: string;
  shortDescription?: string;
  generatedUrl: string;
  createdAt: Date;
  createdBy: string;
}

// A campaign groups related UTM links together
export interface UtmCampaign {
  id: string;
  name: string;
  description?: string;
  color: string;
  status: 'active' | 'paused' | 'completed' | 'draft';
  startDate?: Date;
  endDate?: Date;
  links: UtmLink[];
  notes: UtmNote[];
  tags: string[];
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
}

// A strategy is a collection of campaigns with notes and collaboration
export interface UtmStrategy {
  id: string;
  name: string;
  description?: string;
  campaigns: UtmCampaign[];
  // Collaboration
  sharedWith: string[]; // User IDs
  // Metadata
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
}

// Notes for collaboration within campaigns
export interface UtmNote {
  id: string;
  content: string;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
}

// For drag and drop builder state
export interface UtmBuilderState {
  baseUrl: string;
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
}

// Helper to normalize UTM source names (facebook -> fb, instagram -> ig)
export function normalizeUtmSource(source: string): string {
  const normalized = source.toLowerCase().trim();
  const mappings: Record<string, string> = {
    'facebook': 'fb',
    'instagram': 'ig',
  };
  return mappings[normalized] || source;
}

// Helper function to generate UTM URL
export function generateUtmUrl(builder: UtmBuilderState): string {
  const { baseUrl, source, medium, campaign, term, content } = builder;
  
  if (!baseUrl || !source || !medium || !campaign) {
    return '';
  }
  
  const url = new URL(baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`);
  
  const normalizedSource = normalizeUtmSource(source);
  url.searchParams.set('utm_source', normalizedSource.toLowerCase().replace(/\s+/g, '_'));
  url.searchParams.set('utm_medium', medium.toLowerCase().replace(/\s+/g, '_'));
  url.searchParams.set('utm_campaign', campaign.toLowerCase().replace(/\s+/g, '_'));
  
  if (term) {
    url.searchParams.set('utm_term', term.toLowerCase().replace(/\s+/g, '_'));
  }
  
  if (content) {
    url.searchParams.set('utm_content', content.toLowerCase().replace(/\s+/g, '_'));
  }
  
  return url.toString();
}

// Helper to parse UTM parameters from a URL
export function parseUtmFromUrl(urlString: string): Partial<UtmBuilderState> | null {
  try {
    const url = new URL(urlString);
    return {
      baseUrl: `${url.protocol}//${url.host}${url.pathname}`,
      source: url.searchParams.get('utm_source') || '',
      medium: url.searchParams.get('utm_medium') || '',
      campaign: url.searchParams.get('utm_campaign') || '',
      term: url.searchParams.get('utm_term') || '',
      content: url.searchParams.get('utm_content') || '',
    };
  } catch {
    return null;
  }
}

// Campaign status colors
export const CAMPAIGN_STATUS_COLORS: Record<UtmCampaign['status'], string> = {
  active: '#22c55e',
  paused: '#f59e0b',
  completed: '#6b7280',
  draft: '#3b82f6',
};

// Default campaign colors for visual organization
export const CAMPAIGN_COLORS = [
  '#3b82f6', // Blue
  '#22c55e', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#f97316', // Orange
];
